import { useResponsive } from '../../providers/ResponsiveProvider';
import { __ } from '../../utils';
import {
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';

export type FeedSortOrderValues = {
  orderBy: 'date' | 'location' | 'votes';
  orderDirection: 'asc' | 'desc';
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: FeedSortOrderValues) => void;
  initialValues?: Partial<FeedSortOrderValues>;
};

export const sortOptions = [
  {
    label: __('od najnovších'),
    orderBy: 'date' as const,
    orderDirection: 'desc' as const,
  },
  {
    label: __('od najstarších'),
    orderBy: 'date' as const,
    orderDirection: 'asc' as const,
  },
  {
    label: __('od najbližších'),
    orderBy: 'location' as const,
    orderDirection: 'asc' as const,
  },
  {
    label: __('od najvzdialenejších'),
    orderBy: 'location' as const,
    orderDirection: 'desc' as const,
  },
  {
    label: __('od najviac hlasov'),
    orderBy: 'votes' as const,
    orderDirection: 'desc' as const,
  },
  {
    label: __('od najmenej hlasov'),
    orderBy: 'votes' as const,
    orderDirection: 'asc' as const,
  },
];

const FeedSortOrder: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  initialValues = {
    orderBy: 'location',
    orderDirection: 'asc',
  },
}) => {
  const { isMd } = useResponsive();

  const handleOptionClick = (
    orderBy: FeedSortOrderValues['orderBy'],
    orderDirection: FeedSortOrderValues['orderDirection'],
  ) => {
    onSubmit({ orderBy, orderDirection });
    onClose();
  };

  const isSelected = (
    orderBy: FeedSortOrderValues['orderBy'],
    orderDirection: FeedSortOrderValues['orderDirection'],
  ) => {
    return (
      initialValues.orderBy === orderBy &&
      initialValues.orderDirection === orderDirection
    );
  };

  const content = (
    <Stack
      sx={{
        height: 1,
      }}
    >
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        sx={{
          px: 2,
          py: 1,
        }}
      >
        <Typography
          variant='h6'
          sx={{
            pt: 0.5,
          }}
        >
          {__('Zoradiť')}
        </Typography>

        <IconButton clear onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider />

      <Stack p={2} spacing={1}>
        {sortOptions.map((option) => (
          <Button
            key={`${option.orderBy}-${option.orderDirection}`}
            variant={
              isSelected(option.orderBy, option.orderDirection)
                ? 'contained'
                : 'outlined'
            }
            onClick={() =>
              handleOptionClick(option.orderBy, option.orderDirection)
            }
            fullWidth
          >
            {option.label}
          </Button>
        ))}
      </Stack>
    </Stack>
  );

  return (
    <Drawer
      anchor={isMd ? 'right' : 'bottom'}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: {
            md: 320,
          },
          borderTopLeftRadius: {
            xs: 16,
            md: 0,
          },
          borderTopRightRadius: {
            xs: 16,
            md: 0,
          },
        },
      }}
    >
      {content}
    </Drawer>
  );
};

export default FeedSortOrder;
